import { Box, Stack, styled, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { ComponentProps, FC, forwardRef, ReactNode, useRef } from "react";
import { ArcherContainer, ArcherElement } from "react-archer";
import { useBackfillStore } from "../../store/backfillsStore";

// react-archer's `exports` map blocks deep-importing this type, so derive it from the component.
type RelationType = NonNullable<ComponentProps<typeof ArcherElement>["relations"]>[number];

const getNodeIconColor = (type: string, remark: boolean, theme: Theme) => {
  if (type === "download") {
    return remark ? theme.palette.accentInfo40.main : theme.palette.accentInfo30.main;
  } else {
    return remark ? theme.palette.secondary80.main : theme.palette.secondary30.main;
  }
};

const NodeIcon = styled("div", {
  shouldForwardProp: (prop) => prop !== "type",
})<{ type: string; showIcon: boolean; remark: boolean }>(({ type, showIcon, remark, theme }) => ({
  backgroundColor: getNodeIconColor(type, remark, theme),
  zIndex: theme.zIndex.nodeLabel,
  color: theme.palette.text50.main,
  width: showIcon ? 40 : 5,
  height: showIcon ? 40 : 5,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  "&:hover": {
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  },
}));

const FullScreenButtonContainer = styled(Stack)(({ theme }) => ({
  position: "sticky",
  top: "0",
  bottom: "0",
  zIndex: theme.zIndex["fab"],
}));

const NodeContainer = styled(Box)<{ isClickable: boolean }>(({ isClickable }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  height: 60,
  maxWidth: 100,
  minWidth: 30,
  boxShadow: "none",
  border: "none",
  ...(isClickable && {
    cursor: "pointer",
  }),
}));

const NodeText = styled(Typography)({
  zIndex: 1,
  fontSize: 11,
  maxWidth: "140px",
  textOverflow: "ellipsis",
  overflow: "hidden",
});

interface INodeButtonProps {
  label: string;
  type: "download" | "upload";
  selected?: boolean;
  onClickNode?: (label: string) => void;
  showIcon: boolean;
  remark: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const getSubgraph = (type: "static" | "dynamic") => {
  // We have two graphs, the subgraph that comes from the query, and the currentSubgraph, which contains the information of the actual
  // subgraph including the selected nodes. If type is dynamic, it means we are on the backfill modal,
  // and it means that we are going to need both subgraphs, because we want to know which nodes are selected
  // (that info is in the currentSubgraph), but also we want to know
  // how to draw the graph, and that information is in the subgraph that comes from the query.
  // If type is static, we only need to draw one graph, and the way to draw it is in the currentSubgraph.
  if (type === "dynamic") return useBackfillStore.getState().subgraph;
  return useBackfillStore.getState().currentSubgraph;
};

const getDownloadsFromHoveredUpload = (type: "static" | "dynamic") => {
  const drawedSubgraph = getSubgraph(type);
  const hoveredEl = useBackfillStore.getState().hoveredEl;
  const downloadsFromHoveredUpload: string[] = [];
  Object.keys(drawedSubgraph.links).forEach((downloadName) => {
    drawedSubgraph.links[downloadName].forEach((uploadName) => {
      if (uploadName === hoveredEl) {
        downloadsFromHoveredUpload.push(downloadName);
      }
    });
  });
  return downloadsFromHoveredUpload;
};

const getUploadsFromHoveredDownload = (type: "static" | "dynamic") => {
  const drawedSubgraph = getSubgraph(type);
  const hoveredEl = useBackfillStore.getState().hoveredEl;
  const uploadsFromHoveredDownload: string[] = [];
  Object.keys(drawedSubgraph.links).forEach((downloadName) => {
    if (downloadName === hoveredEl) {
      drawedSubgraph.links[downloadName].forEach((uploadName) => {
        uploadsFromHoveredDownload.push(uploadName);
      });
    }
  });
  return uploadsFromHoveredDownload;
};

const getStatus = (download: string, upload: string) => {
  const currentSubgraph = useBackfillStore.getState().currentSubgraph;
  const hoveredEl = useBackfillStore.getState().hoveredEl;
  const isHovered = hoveredEl === download || hoveredEl === upload;
  const isInCurrentSubgraph = currentSubgraph?.export_names.includes(upload);
  return { isHovered, isInCurrentSubgraph };
};

const getStroke = (downloadName: string, uploadName: string, type: string, theme: Theme) => {
  const { isHovered, isInCurrentSubgraph } = getStatus(downloadName, uploadName);
  const stroke = { color: theme.palette.text30.main, width: 1 };
  if (type === "static" && isHovered) {
    stroke.width = 1.5;
  } else {
    if (isHovered || isInCurrentSubgraph) {
      stroke.width = 1;
    } else {
      stroke.color = theme.palette.surfaceLight30.main;
    }
  }
  return stroke;
};

const NodeButton = forwardRef<HTMLDivElement, INodeButtonProps>(
  ({ label, type, selected, onClickNode, showIcon, remark, ...rest }, ref) => {
    const theme = useTheme();
    const iconComponent = {
      download: <DownloadSimpleIcon color={theme.palette.text10.main} />,
      upload: <UploadSimpleIcon color={theme.palette.text10.main} />,
    };
    return (
      <NodeContainer
        {...rest}
        onClick={() => (onClickNode ? onClickNode(label) : null)}
        data-cy={selected ? `node-${label}-selected` : `node-${label}`}
        isClickable={!!onClickNode}
      >
        {type === "download" ? (
          <Tooltip title={label}>
            <NodeText sx={{ fontWeight: remark || selected ? "bold" : "normal" }}>{label}</NodeText>
          </Tooltip>
        ) : null}

        <NodeIcon ref={ref} type={type} showIcon={showIcon} remark={remark || Boolean(selected)}>
          {showIcon ? iconComponent[type] : null}
        </NodeIcon>
        {type === "upload" ? (
          <Tooltip title={label}>
            <NodeText sx={{ fontWeight: remark || selected ? "bold" : "normal" }}>{label}</NodeText>
          </Tooltip>
        ) : null}
      </NodeContainer>
    );
  }
);
NodeButton.displayName = "NodeButton"; // https://reactjs.org/docs/react-component.html#displayname

interface ICommonDiagram {
  fullWidth?: boolean;
  fullScreenButton: ReactNode;
  onClickNode?: (label: string) => void;
  showIcons: boolean;
  type: "dynamic" | "static";
}

const DownloadNode = ({
  downloadName,
  showIcons,
  type,
}: {
  downloadName: string;
  showIcons: boolean;
  type: "dynamic" | "static";
}) => {
  const drawedSubgraph = getSubgraph(type);
  const hoveredEl = useBackfillStore((state) => state.hoveredEl);
  const setHoveredEl = useBackfillStore((state) => state.setHoveredEl);
  const downloadsFromHoveredUpload = getDownloadsFromHoveredUpload(type);
  const theme = useTheme();

  const getRelations = () => {
    const relations: RelationType[] = [];
    const bundleName = downloadName.split(".")[0]; // If it is a bundle, it will be bundleName of bundleName.partName
    drawedSubgraph.links[bundleName].forEach((uploadName) => {
      relations.push({
        targetId: uploadName,
        targetAnchor: "top",
        sourceAnchor: "bottom",
        style: {
          strokeWidth: getStroke(downloadName, uploadName, type, theme).width,
          strokeColor: getStroke(downloadName, uploadName, type, theme).color,
        },
      });
    });

    return relations;
  };
  return (
    <ArcherElement key={downloadName} id={`import_${downloadName}`} relations={getRelations()}>
      <NodeButton
        key={downloadName}
        type="download"
        label={downloadName}
        onMouseEnter={() => setHoveredEl(downloadName)}
        onMouseLeave={() => setHoveredEl("")}
        showIcon={showIcons}
        remark={downloadsFromHoveredUpload.includes(downloadName) || hoveredEl === downloadName}
      />
    </ArcherElement>
  );
};

const Downloads = ({ showIcons, type }: { showIcons: boolean; type: "dynamic" | "static" }) => {
  const drawedSubgraph = getSubgraph(type);
  return (
    <Stack direction="row" spacing={5} sx={{ justifyContent: "space-around", flexWrap: "wrap" }}>
      {drawedSubgraph.import_names?.map((downloadName) => {
        return <DownloadNode key={downloadName} downloadName={downloadName} showIcons={showIcons} type={type} />;
      })}
    </Stack>
  );
};

const UploadNode = ({
  uploadName,
  onClickNode,
  showIcons,
  type,
}: {
  uploadName: string;
  onClickNode?: (label: string) => void;
  showIcons: boolean;
  type: "dynamic" | "static";
}) => {
  const hoveredEl = useBackfillStore((state) => state.hoveredEl);
  const setHoveredEl = useBackfillStore((state) => state.setHoveredEl);
  const currentSubgraph = useBackfillStore((state) => state.currentSubgraph);
  const uploadsFromHoveredDownload = getUploadsFromHoveredDownload(type);
  return (
    <ArcherElement id={uploadName} key={uploadName}>
      <NodeButton
        type="upload"
        label={uploadName}
        onMouseEnter={() => setHoveredEl(uploadName)}
        onMouseLeave={() => setHoveredEl("")}
        selected={currentSubgraph?.export_names.includes(uploadName)}
        onClickNode={onClickNode}
        showIcon={showIcons}
        remark={uploadsFromHoveredDownload.includes(uploadName) || hoveredEl === uploadName}
      />
    </ArcherElement>
  );
};

const Uploads = ({
  onClickNode,
  showIcons,
  type,
}: {
  onClickNode?: (label: string) => void;
  showIcons: boolean;
  type: "dynamic" | "static";
}) => {
  const drawedSubgraph = getSubgraph(type);
  return (
    <Stack direction="row" spacing={6} sx={{ justifyContent: "center", flexWrap: "wrap" }}>
      {drawedSubgraph.export_names?.map((uploadName) => {
        return (
          <UploadNode
            key={uploadName}
            uploadName={uploadName}
            onClickNode={onClickNode}
            showIcons={showIcons}
            type={type}
          />
        );
      })}
    </Stack>
  );
};

const CommonDiagram: FC<ICommonDiagram> = ({ fullWidth, fullScreenButton, onClickNode, showIcons, type }) => {
  const ref = useRef(null);

  return (
    <>
      <FullScreenButtonContainer>{!fullWidth && fullScreenButton}</FullScreenButtonContainer>
      <ArcherContainer ref={ref}>
        <Stack
          direction="column"
          spacing={10}
          sx={{ width: "100%", minHeight: "90%", pr: 3, pl: 3, justifyContent: "space-between" }}
        >
          <Downloads showIcons={showIcons} type={type} />
          <Uploads onClickNode={onClickNode} showIcons={showIcons} type={type} />
        </Stack>
      </ArcherContainer>
    </>
  );
};

export { CommonDiagram };
